import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to track a container's dimensions with responsive updates
 * @param {string} containerSelector - CSS selector for the container to measure
 * @param {number} defaultWidth - Default width to use if container not found
 * @param {number} defaultHeight - Default height to use if container not found
 * @param {number} maxWidth - Maximum width constraint
 * @param {number} maxHeight - Maximum height constraint
 * @returns {{ width: number, height: number }} - The current container dimensions
 */
export const useContainerDimensions = (
  containerSelector: string,
  defaultWidth: number = 400,
  defaultHeight: number = 400,
  maxWidth: number = 600,
  maxHeight: number = 800
): { width: number; height: number } => {
  const [containerDimensions, setContainerDimensions] = useState({
    width: defaultWidth,
    height: defaultHeight
  });
  const resizeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const updateDimensions = () => {
      // Clear any pending timeouts to avoid multiple updates
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }

      // Use a small timeout to avoid excessive calculations during resize
      resizeTimeoutRef.current = window.setTimeout(() => {
        const container = document.querySelector(containerSelector);
        if (container) {
          const newWidth = Math.min(container.clientWidth, maxWidth);
          const newHeight = Math.min(container.clientHeight, maxHeight);
          
          setContainerDimensions({
            width: newWidth,
            height: newHeight
          });
        } else {
          setContainerDimensions({
            width: defaultWidth,
            height: defaultHeight
          });
        }
        
        resizeTimeoutRef.current = null;
      }, 100);
    };

    // Initial dimensions calculation
    updateDimensions();

    // Set up listener for window resize events
    window.addEventListener("resize", updateDimensions);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener("resize", updateDimensions);
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [containerSelector, defaultWidth, defaultHeight, maxWidth, maxHeight]);

  return containerDimensions;
};